import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { FM210, FM211, FM212 } from '../../services/m207Api';
import { useM207Mode } from './hooks/useM207Mode';
import { useM207Record } from './hooks/useM207Record';
import { useM207Navigation } from './hooks/useM207Navigation';
import { SharedActionBar } from '../../components/SharedActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import './M207Form.css';

const M207Form: React.FC = () => {
  const navigate = useNavigate();

  // Hooks
  const { mode, setMode, isEditing } = useM207Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow, updateBoardNumbers,
  } = useM207Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useM207Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load M207 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await handleSave(mode, async (result: FM210) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fm210_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await handleSave(mode, async (result: FM210) => {
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除修改單 ${record?.fm210_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, setMode, navigate]);

  // Save (for SharedActionBar onSave)
  const handleSaveAction = useCallback(async () => {
    if (mode === 'add') {
      await handleAddAction();
    } else if (mode === 'edit') {
      await handleModifyAction();
    }
  }, [mode, handleAddAction, handleModifyAction]);

  // Cancel (for SharedActionBar onCancel)
  const handleCancelAction = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  /** Handle detail field changes */
  const handleDetailChange = useCallback((idx: number, key: keyof FM211, value: FM211[keyof FM211]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  /** Handle board number changes for a detail row */
  const handleBoardNumbersChange = useCallback((detailIdx: number, boardNumbers: Partial<FM212>[]) => {
    updateBoardNumbers(detailIdx, boardNumbers);
  }, [updateBoardNumbers]);

  // Status code for approval display
  const statusCode = (record?.fm210_80 || '').trim();

  return (
    <div className="m207-form">
      <div className="page-wrapper">
        {/* Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">庫位板號修改單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>庫位板號修改單 (pwm207f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fm210_01 || '(新增)'}
            </span>
          </div>
        </div>

        {/* Single screen — no tabs (FoxPro pageCount=0) */}
        <div className="form-content">
          <Tab1BasicInfo
            formData={formData}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
            isApproved={statusCode === 'P'}
          />
          <Tab2Details
            details={details}
            isEditing={isEditing}
            onAdd={addDetailRow}
            onDelete={deleteDetailRow}
            onDetailChange={handleDetailChange}
            onBoardNumbersChange={handleBoardNumbersChange}
            totalCount={Number(getField('fm210_15')) || details.length}
          />
        </div>

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAddAction}
          onModify={() => { if (!record) return; setMode('edit'); }}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancelAction}
          approval={{
            statusCode,
            isApproved: statusCode === 'P',
            isPending: false,
            isLocked: statusCode === 'P',
          }}
          onExit={handleExit}
        />
      </div>
    </div>
  );
};

export default M207Form;
